import { useEffect, useCallback, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import endpoints from '@app/constants/endpoints';
import { isHomepage, replaceTokensInString, isEmpty } from '@app/utils';
import { getPageRedirectURL } from '@app/services/windowService';
import { useHttp } from '@app/hooks';
import { ModalContext } from 'cat-ecommerce-alloy';
import { SHOW_LOGIN_EVENT } from '@app/constants/events';
import { createFragmentCookie } from '../../utils';
import { CODE_TIMEOUT, getErrorStatusCode } from '@app/store/exception/utils';
import { setError, clearError } from '@app/store/exception/actions';
import {
  ERROR_DOMAIN,
  ERROR_PATH,
  INLINE_NOTIFICATION_ERROR_INFO,
  LOGIN,
  TIMEOUT_ERROR
} from '@app/constants/errorConstants';
import useAnalytics from '@app/hooks/useAnalytics';
import {
  EVENT_CRITICAL_ERROR,
  EVENT_TIMEOUT_ERROR
} from '@app/constants/analyticsConstants';

const { LOGIN_B2C_URL } = endpoints;

const useLogin = () => {
  const dispatch = useDispatch();
  const { storeId, langId } = useSelector(s => s.common);
  const isCustomerSupportErrorEventLoggingEnabled = useSelector(
    s => s.featureFlag?.PCC_CustomerSupportErrorEventLoggingRegAndLogin
  );
  const { invoke, loading } = useHttp();
  const ctx = useContext(ModalContext);
  const encodeRedirectUrl = url => {
    const redirectUrl = !!url ? encodeURIComponent(url) : '';
    return redirectUrl;
  };
  const { fireErrorTrackingEvent } = useAnalytics();

  /* when called, we would punchout to b2c*/
  const login = useCallback(
    detailEvent => {
      /*Added pageRedirectURL for AZURE-203120 : This will have a value when the user 
      navigates using the email links and then tries to log in. */
      const pageRedirectURL = encodeRedirectUrl(getPageRedirectURL());
      const defaultRedirectUrl = encodeRedirectUrl(
        detailEvent?.detail?.redirectUrl
      );
      // Clear the Login Registration Errors if any
      dispatch(clearError(ERROR_DOMAIN.LOGIN_REGISTRATION));
      const endpointURL = replaceTokensInString(
        LOGIN_B2C_URL,
        storeId,
        langId,
        !isEmpty(defaultRedirectUrl) ? defaultRedirectUrl : pageRedirectURL,
        isHomepage()
      );
      createFragmentCookie();
      invoke({ method: 'get', rethrowError: true, url: endpointURL })
        .then(
          data => {
            window.location = data.url;
          },
          err => {
            const { code } = err;
            if (isCustomerSupportErrorEventLoggingEnabled && isHomepage()) {
              if (code === CODE_TIMEOUT) {
                fireErrorTrackingEvent(
                  EVENT_TIMEOUT_ERROR,
                  getErrorStatusCode(err),
                  LOGIN
                );
                const actions = [
                  {
                    buttonText: 'RETRY',
                    action: () => login(detailEvent)
                  }
                ];
                dispatch(
                  setError(
                    ERROR_DOMAIN.LOGIN_REGISTRATION,
                    ERROR_PATH.TIMEOUT,
                    {
                      ...TIMEOUT_ERROR,
                      actions
                    }
                  )
                );
              } else {
                fireErrorTrackingEvent(
                  EVENT_CRITICAL_ERROR,
                  getErrorStatusCode(err),
                  LOGIN
                );
                dispatch(
                  setError(
                    ERROR_DOMAIN.LOGIN_REGISTRATION,
                    ERROR_PATH.LOGIN_INLINE_ERROR,
                    {
                      ...INLINE_NOTIFICATION_ERROR_INFO,
                      detailedError: LOGIN
                    }
                  )
                );
              }
            }
          }
        )
        .finally(() => ctx.closeAllModals());
    },
    [
      dispatch,
      storeId,
      langId,
      invoke,
      isCustomerSupportErrorEventLoggingEnabled,
      fireErrorTrackingEvent,
      ctx
    ]
  );
  useEffect(() => {
    document.addEventListener(SHOW_LOGIN_EVENT, login);
    return () => document.removeEventListener(SHOW_LOGIN_EVENT, login);
  }, [login]);

  return { login, loading };
};
export default useLogin;
